/**
 * BookingXmlDetails
 *
 * Read-only projection of a single booking record's stored XML/parsed
 * fields. This is display-only and never mutates the record. It relies
 * exclusively on the fields the MMT parser and Google Sheets sync already
 * persist (`vendor_booking_id`, `net_amount`, `payment`, `price_details`,
 * `room_breakup`, `cancellation_policy`, `add_ons`, `raw_xml`,
 * `raw_data`, …). No MMT parser/sync code is touched.
 *
 * Renders three collapsible surfaces per record:
 *   1. Booking Details — a large key/value grid of common fields.
 *   2. Structured Data — payment / price / cancellation / room breakup arrays.
 *   3. Raw XML Response — the exact string stored on the record.
 */
import React from 'react';

const currency = (v, ccy = '') => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(n)) return String(v);
  return `${ccy ? ccy + ' ' : '₹'}${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const show = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? `${v.length} item${v.length > 1 ? 's' : ''}` : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// Ordered pick — for the human-friendly grid.
const FIELD_ROWS = [
  // Identity
  ['Vendor Booking ID',     (r) => r.vendor_booking_id || r.raw_data?.['Vendor Booking ID']],
  ['Internal Booking ID',   (r) => r.booking_id || r.raw_data?.['Booking/Reservation ID']],
  ['Original Booking ID',   (r) => r.original_booking_id || r.raw_data?.['Original Booking ID']],
  ['Status',                (r) => r.status || r.raw_data?.Status],
  ['MMT Status',            (r) => r.raw_status || r.raw_data?.['MMT Status']],
  ['Booking Vendor',        (r) => r.booking_vendor_name],
  // Dates
  ['Booking Date',          (r) => r.booking_date || r.raw_data?.['Booked/Email Date']],
  ['Created Time',          (r) => r.created_time],
  ['Last Modified',         (r) => r.last_modified_time],
  ['Check-In',              (r) => r.check_in_date || r.raw_data?.['Check-In']],
  ['Check-Out',             (r) => r.check_out_date || r.raw_data?.['Check-Out']],
  ['Expected Arrival',      (r) => r.expected_arrival_time],
  // Property
  ['Hotel Code',            (r) => r.hotel_code],
  ['Hotel Name',            (r) => r.hotel_name || r.raw_data?.['Hotel Name']],
  ['Property ID',           (r) => r.property_id],
  // Room & rate
  ['Room Type',             (r) => r.room_type_name || r.raw_data?.['Room Type']],
  ['Room Type Code',        (r) => r.room_type_code],
  ['Rate Plan',             (r) => r.rate_plan_name],
  ['Rate Plan Code',        (r) => r.rate_plan_code],
  ['Rate Plan Type',        (r) => r.rate_plan_type || r.raw_data?.['Rate Plan Type'] || r.external_ids?.yatra_rate_plan_type],
  ['Rate Plan ID',          (r) => r.raw_data?.['Rate Plan ID'] || r.external_ids?.yatra_rate_plan_id],
  ['Room ID',               (r) => r.raw_data?.['Room ID'] || r.external_ids?.yatra_room_id],
  ['Contract Type',         (r) => r.contract_type],
  ['Room Nights',           (r) => r.number_of_nights || r.nights || r.raw_data?.['Room Nights']],
  ['No. of Rooms',          (r) => r.number_of_rooms || r.rooms || r.raw_data?.['No. of Rooms']],
  ['Adults',                (r) => r.adults ?? r.raw_data?.Adults],
  ['Children',              (r) => r.children ?? r.raw_data?.Children],
  ['Guests',                (r) => r.guests],
  // Guest
  ['Guest Name',            (r) => r.guest_name || r.raw_data?.['Guest Name']],
  ['Guest Email',           (r) => r.guest_email || r.raw_data?.['Guest Email']],
  ['Guest Phone',           (r) => r.guest_phone || r.raw_data?.['Guest Phone']],
  ['Customer GST Info',     (r) => r.customer_gst_info],
  ['Special Request',       (r) => r.special_request],
  // Financial
  ['Currency',              (r) => r.currency || r.currency_name || r.raw_data?.Currency],
  ['Price',                 (r) => r.price],
  ['Total Amount',          (r) => r.total_amount],
  ['Net Amount',            (r) => r.net_amount ?? r.raw_data?.['Net Amount']],
  ['Tax/GST',               (r) => r.tax_amount ?? r.raw_data?.['Tax/GST']],
  ['Yatra Total Bill Amount', (r) => r.raw_data?.['Yatra Total Bill Amount'] ?? r.external_ids?.yatra_total_bill_amount],
  ['Yatra Prepay Amount',   (r) => r.raw_data?.['Yatra Prepay Amount'] ?? r.external_ids?.yatra_total_prepay_amount],
  ['Yatra GST',             (r) => r.raw_data?.['Yatra GST'] ?? r.external_ids?.yatra_gst],
  ['Cancellation Policy',   (r) => r.cancellation_policy || r.cancellation?.policy || r.raw_data?.['Cancellation Policy']],
  ['Discount',              (r) => r.raw_data?.Discount || r.external_ids?.yatra_discounts],
  ['Promotion',             (r) => r.raw_data?.Promotion || r.external_ids?.yatra_promotions],
  ['Commission',            (r) => r.commission],
  ['Pay At Hotel',          (r) => r.pay_at_hotel_flag],
  ['Tax Payable At Hotel',  (r) => r.tax_payable_at_hotel],
  // OTA
  ['OTA',                   (r) => r.ota || r.raw_data?.OTA],
  ['Source',                (r) => r.source],
  ['Synced At',             (r) => r.synced_at],
];

function DetailsGrid({ record, testidPrefix }) {
  const rows = FIELD_ROWS
    .map(([label, get]) => [label, get(record)])
    .filter(([, v]) => v !== undefined && v !== null && v !== '');

  if (rows.length === 0) {
    return (
      <div className="text-xs text-gray-400 italic py-2" data-testid={`${testidPrefix}-empty`}>
        No additional fields recorded on this booking.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs"
         data-testid={`${testidPrefix}-fields`}>
      {rows.map(([label, value]) => (
        <div key={label} className="flex flex-col border-b border-gray-100 py-1">
          <span className="text-[10px] uppercase tracking-wide text-gray-400">{label}</span>
          <span className="text-gray-800 break-words">{show(value)}</span>
        </div>
      ))}
    </div>
  );
}

function StructuredCollections({ record, testidPrefix }) {
  const pd = record.price_details;
  const payment = record.payment;
  const room_breakup = record.room_breakup;
  const day_room_breakup = record.day_room_breakup;
  const room_stay = record.room_stay;
  const cancellation_policy = record.cancellation_policy;
  const add_ons = record.add_ons;
  const pre_buy_details = record.pre_buy_details;
  const inclusions = record.inclusions;

  const hasAny = [pd, payment, room_breakup, day_room_breakup, room_stay,
                  cancellation_policy, add_ons, pre_buy_details, inclusions]
    .some((v) => (Array.isArray(v) ? v.length > 0 : (v && Object.keys(v || {}).length > 0)));

  if (!hasAny) return null;

  return (
    <div className="mt-3 space-y-2" data-testid={`${testidPrefix}-structured`}>
      {pd && Object.keys(pd).length > 0 && (
        <SubBlock title="Price Details" testid={`${testidPrefix}-price-details`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {Object.entries(pd).map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase text-gray-400">{k}</div>
                <div className="text-gray-800">{show(v)}</div>
              </div>
            ))}
          </div>
        </SubBlock>
      )}

      {payment && (
        <SubBlock title="Payment" testid={`${testidPrefix}-payment`}>
          <PayloadJson value={payment} />
        </SubBlock>
      )}

      {Array.isArray(room_breakup) && room_breakup.length > 0 && (
        <SubBlock title={`Room Breakup (${room_breakup.length})`} testid={`${testidPrefix}-room-breakup`}>
          <PayloadJson value={room_breakup} />
        </SubBlock>
      )}

      {Array.isArray(day_room_breakup) && day_room_breakup.length > 0 && (
        <SubBlock title={`Day-wise Room Breakup (${day_room_breakup.length})`}
                  testid={`${testidPrefix}-day-room-breakup`}>
          <PayloadJson value={day_room_breakup} />
        </SubBlock>
      )}

      {Array.isArray(room_stay) && room_stay.length > 0 && (
        <SubBlock title={`Room Stay (${room_stay.length})`} testid={`${testidPrefix}-room-stay`}>
          <PayloadJson value={room_stay} />
        </SubBlock>
      )}

      {Array.isArray(cancellation_policy) && cancellation_policy.length > 0 && (
        <SubBlock title={`Cancellation Policy (${cancellation_policy.length})`}
                  testid={`${testidPrefix}-cancellation-policy`}>
          <PayloadJson value={cancellation_policy} />
        </SubBlock>
      )}

      {Array.isArray(add_ons) && add_ons.length > 0 && (
        <SubBlock title={`Add-ons (${add_ons.length})`} testid={`${testidPrefix}-add-ons`}>
          <PayloadJson value={add_ons} />
        </SubBlock>
      )}

      {Array.isArray(pre_buy_details) && pre_buy_details.length > 0 && (
        <SubBlock title={`Pre-buy Details (${pre_buy_details.length})`}
                  testid={`${testidPrefix}-pre-buy`}>
          <PayloadJson value={pre_buy_details} />
        </SubBlock>
      )}

      {Array.isArray(inclusions) && inclusions.length > 0 && (
        <SubBlock title={`Inclusions (${inclusions.length})`} testid={`${testidPrefix}-inclusions`}>
          <ul className="list-disc pl-5 text-xs text-gray-700">
            {inclusions.map((it, i) => <li key={i}>{show(it)}</li>)}
          </ul>
        </SubBlock>
      )}
    </div>
  );
}

function RawXmlBlock({ record, testidPrefix }) {
  // MMT stores the raw XML at `raw_xml`; Yatra CHM-Push stores it at
  // `raw_payload.xml` (mirrored in `raw_data["Raw Yatra Payload"].xml`).
  const xml = record?.raw_xml
    || record?.raw_payload?.xml
    || record?.raw_data?.['Raw Yatra Payload']?.xml;
  if (!xml || typeof xml !== 'string' || !xml.trim()) return null;
  return (
    <details className="mt-3 border rounded bg-slate-50" data-testid={`${testidPrefix}-raw-xml`}>
      <summary className="cursor-pointer px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-slate-100">
        Raw XML Response · {xml.length.toLocaleString()} chars
      </summary>
      <pre className="text-[10px] leading-relaxed p-3 max-h-96 overflow-auto whitespace-pre-wrap break-all bg-white text-gray-800">
        {xml}
      </pre>
    </details>
  );
}

function PayloadJson({ value }) {
  let text;
  try { text = JSON.stringify(value, null, 2); } catch (_) { text = String(value); }
  return (
    <pre className="text-[10px] leading-relaxed p-2 max-h-64 overflow-auto whitespace-pre-wrap break-words bg-white border rounded text-gray-800">
      {text}
    </pre>
  );
}

function SubBlock({ title, testid, children }) {
  return (
    <details className="border rounded bg-white" data-testid={testid}>
      <summary className="cursor-pointer px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50">
        {title}
      </summary>
      <div className="p-2">{children}</div>
    </details>
  );
}

export function BookingXmlDetails({ record, testidPrefix = 'booking-detail' }) {
  if (!record) return null;
  return (
    <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
      <details className="rounded border bg-gray-50" open data-testid={`${testidPrefix}-all-fields`}>
        <summary className="cursor-pointer px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100">
          All available booking fields
        </summary>
        <div className="p-3">
          <DetailsGrid record={record} testidPrefix={testidPrefix} />
        </div>
      </details>

      <StructuredCollections record={record} testidPrefix={testidPrefix} />
      <RawXmlBlock record={record} testidPrefix={testidPrefix} />
    </div>
  );
}

// Rely on the default export purely for backward-compatible imports.
export default BookingXmlDetails;

// Bonus: currency helper — exported to keep the file self-contained,
// otherwise unused externally.
export const _formatBookingCurrency = currency;
